import { useState } from "react";
import { useResource, formatDate, titleCase } from "../lib/hooks";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { api } from "../lib/api";
import type { Participant } from "../lib/types";

export interface PartnerRequest {
    id: string;
    /** Filing reference the partner can quote, e.g. TC-REQ-2026-00001. */
    ref_code: string | null;
    kind: "referral" | "shift_cover" | "quote" | "service" | "complaint" | "incident" | "inquiry" | "worker_request";
    subject: string;
    details: string;
    services: string[];
    urgency: "routine" | "high" | "urgent";
    status: "new" | "in_review" | "info_requested" | "actioned" | "declined";
    response: string | null;
    participant_id: string | null;
    created_by_name: string | null;
    decided_by_name: string | null;
    created_at: string;
    decided_at: string | null;
}

const KINDS = [
    { value: "referral", label: "Refer a participant" },
    { value: "worker_request", label: "Request a worker" },
    { value: "shift_cover", label: "Request shift cover" },
    { value: "quote", label: "Request a quote" },
    { value: "service", label: "Request a service" },
    { value: "complaint", label: "Lodge a complaint" },
    { value: "incident", label: "Lodge an incident" },
    { value: "inquiry", label: "General enquiry" },
];


const statusChip = (status: PartnerRequest["status"]): string =>
    status === "actioned" ? "pass" : status === "declined" ? "block"
    : status === "in_review" ? "info" : "warn";

const STATUS_LABELS: Record<PartnerRequest["status"], string> = {
    new: "New",
    in_review: "In Review",
    info_requested: "Information Requested",
    actioned: "Actioned",
    declined: "Declined",
};

/**
 * One queue, two audiences. A partner submits and watches the outcome; a
 * manager works the queue and answers. The rows are the same table under RLS —
 * a partner's list is only ever their own organisation's.
 */
export const PartnerRequests = () => {
    const { user } = useAuth();
    const isManager = user?.role === "admin" || user?.role === "manager";

    const requests = useResource<PartnerRequest[]>("/partner-requests");
    const participants = useResource<Participant[]>("/participants");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [creating, setCreating] = useState(false);
    const [responding, setResponding] = useState<PartnerRequest | null>(null);

    const refresh = () => { requests.reload(); setCreating(false); setResponding(null); };

    const fields: Field[] = [
        { name: "kind", label: "What do you need?", kind: "select", required: true, options: KINDS },
        { name: "subject", label: "Subject", kind: "text", required: true },
        { name: "participantId", label: "About client (optional)", kind: "select",
          options: (participants.data ?? []).map((p) => ({ value: p.id, label: p.full_name })), half: true },
        { name: "urgency", label: "Urgency", kind: "select", half: true, options: [
            { value: "routine", label: "Routine" },
            { value: "high", label: "High" },
            { value: "urgent", label: "Urgent" },
        ] },
        { name: "services", label: "Services (for referrals, quotes and service requests)",
          kind: "multiselect", optionsFrom: { path: "/referral-services", addLabel: "Add service" } },
        { name: "details", label: "Details", kind: "textarea", required: true,
          help: "For referrals include the participant's details and needs; for incidents, what happened and when." },
    ];

    const all = requests.data ?? [];
    const filtered = all.filter((request) =>
        matches(search, request.ref_code, request.subject, request.details, request.created_by_name) &&
        (!filters.kind || request.kind === filters.kind) &&
        (!filters.status || request.status === filters.status)
    );

    if (requests.error) return <div className="error-banner">{requests.error}</div>;

    return (
        <div>
            {creating && (
                <EntityForm
                    title="New Request" path="/partner-requests" fields={fields}
                    subtitle="Goes straight to the TrustCare team. You will see the status and our response here."
                    onClose={() => setCreating(false)} onSaved={refresh} />
            )}
            {responding && (
                <RespondDialog request={responding} onClose={() => setResponding(null)} onDone={refresh} />
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search requests…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "kind", allLabel: "All Types", options: KINDS },
                    { name: "status", allLabel: "All Status", options: [
                        { value: "new", label: "New" },
                        { value: "in_review", label: "In review" },
                        { value: "info_requested", label: "Information requested" },
                        { value: "actioned", label: "Actioned" },
                        { value: "declined", label: "Declined" },
                    ] },
                ]}
                action={!isManager && (
                    <button className="btn" onClick={() => setCreating(true)}>+ New Request</button>
                )}
            />

            <div style={{ display: "grid", gap: "0.85rem" }}>
                {filtered.map((request) => (
                    <div key={request.id} className="card">
                        <div className="card-head">
                            <div>
                                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                    {request.ref_code && (
                                        <span className="chip mute mono" style={{ fontWeight: 600 }}>{request.ref_code}</span>
                                    )}
                                    <span className="chip info">{titleCase(request.kind)}</span>
                                    {request.urgency !== "routine" && (
                                        <span className={`chip ${request.urgency === "urgent" ? "block" : "warn"}`}>
                                            {request.urgency}
                                        </span>
                                    )}
                                    <strong>{request.subject}</strong>
                                </div>
                                <div style={{ fontSize: "0.75rem", color: "var(--muted)", marginTop: 3 }}>
                                    <InitialsAvatar name={request.created_by_name} size={20} />
                                    {request.created_by_name ?? "Unknown"} · {formatDate(request.created_at)}
                                    {request.services.length > 0 && ` · ${request.services.join(", ")}`}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                                <span className={`chip pill ${statusChip(request.status)}`}>
                                    {STATUS_LABELS[request.status] ?? titleCase(request.status)}
                                </span>
                                {isManager && request.status !== "actioned" && request.status !== "declined" && (
                                    <button className="icon-btn" onClick={() => setResponding(request)}>Respond</button>
                                )}
                            </div>
                        </div>
                        <div className="card-body">
                            <p style={{ fontSize: "0.86rem", color: "var(--ink-soft)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                                {request.details}
                            </p>
                            {request.response && (
                                <div className="asked-q-answer" style={{ marginTop: "0.7rem" }}>
                                    <span className="answered-label">
                                        {request.status === "info_requested" ? "Information requested" : "Response"}
                                        {request.decided_by_name ? ` — ${request.decided_by_name}` : ""}
                                        {request.decided_at ? ` · ${formatDate(request.decided_at)}` : ""}
                                    </span>
                                    {request.response}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="card"><div className="card-body empty">
                        {all.length === 0
                            ? (isManager ? "No partner requests yet." : "Nothing here yet — use New Request to ask us for anything we offer.")
                            : "No requests match."}
                    </div></div>
                )}
            </div>
        </div>
    );
};

const RespondDialog = ({ request, onClose, onDone }: {
    request: PartnerRequest; onClose: () => void; onDone: () => void;
}) => {
    const [status, setStatus] = useState<"in_review" | "info_requested" | "actioned" | "declined">("actioned");
    const [response, setResponse] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const responseRequired = status === "declined" || status === "info_requested";

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await api.post(`/partner-requests/${request.id}/respond`, {
                status, response: response.trim() || undefined,
            });
            onDone();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not respond");
            setBusy(false);
        }
    };

    return (
        <Modal title={`Respond — ${request.subject}`}
            subtitle="Your response is shown to the partner. Actioning a referral or quote still happens in the relevant module — this closes the loop with the customer."
            onClose={onClose}
            footer={<>
                <button className="btn" onClick={() => void submit()}
                    disabled={busy || (responseRequired && response.trim().length < 5)}>
                    {busy ? "Saving…" : "Send response"}
                </button>
                <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </>}>
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field">
                    <label htmlFor="resp-status">Outcome</label>
                    <select id="resp-status" value={status}
                        onChange={(e) => setStatus(e.target.value as typeof status)}>
                        <option value="in_review">In review — we are looking at it</option>
                        <option value="info_requested">Information requested — we need more detail</option>
                        <option value="actioned">Actioned — done or underway</option>
                        <option value="declined">Declined</option>
                    </select>
                </div>
                <div className="field">
                    <label htmlFor="resp-text">Response to the partner</label>
                    <textarea id="resp-text" value={response} maxLength={4000}
                        onChange={(e) => setResponse(e.target.value)}
                        placeholder={status === "declined" ? "Required when declining."
                            : status === "info_requested" ? "Required — say exactly what you need from them."
                            : "Optional but appreciated."} />
                </div>
            </div>
        </Modal>
    );
};
